import React, { useEffect, useRef } from 'react';
import useSidePanel from 'talent_matching/hooks/use_side_panel';
import { useRediscoveryApplications } from 'talent_matching/talent_rediscovery/hooks/use_rediscovery_applications';
import { useRediscoveryColumns } from 'talent_matching/talent_rediscovery/components/table/rediscovery_columns';
import BaseDataTable from 'talent_matching/components/shared/base_data_table';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import {
  GridColumnVisibilityModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import NoResultsOverlay from 'talent_matching/components/shared/no_results_overlay';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.rediscovery.empty_state');
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { useUserContext, TABS } from 'talent_matching/contexts/user_context';
import TalentRediscoverySidePanel from 'talent_matching/components/side_panel/talent_rediscovery_side_panel';
import { Person } from 'talent_matching/types';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import useFilterTableState from 'talent_matching/hooks/use_filter_table_state';
import { RediscoveryRowActionsModalProvider } from 'talent_matching/contexts/rediscovery_row_actions_modal_context';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import { OPENSEARCH_MAX_RESULT_WINDOW } from 'talent_matching/opensearch_constraints';

type RediscoveryTableWrapperProps = {
  onTotalRowsChange?: (totalRows: number) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  hasAnyFilters?: boolean;
  onClearAll?: () => void;
  onRowSelected?: (selectedRows: GridRowSelectionModel) => void;
  onClearSelections?: () => void;
  clearSelectionsKey?: number;
};

const RediscoveryTableWrapper = ({
  onTotalRowsChange,
  onLoadingChange,
  columnVisibilityModel,
  setColumnVisibilityModel,
  hasAnyFilters = false,
  onClearAll,
  onRowSelected,
  onClearSelections,
  clearSelectionsKey,
}: RediscoveryTableWrapperProps) => {
  const {
    paths: { rediscoveryApplicationsPath },
  } = useRediscoveryTabContext();
  const { userDateFormat } = useUserContext();

  const {
    data: tableData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useRediscoveryApplications(rediscoveryApplicationsPath as string);

  const rows = tableData?.rows || [];
  const totalRows = tableData?.totalRows || 0;
  // Pager can only reach rows within OpenSearch's max_result_window (GREEN-67215),
  // but the header/callback should still reflect the true match count.
  const pagerTotalRows = Math.min(totalRows, OPENSEARCH_MAX_RESULT_WINDOW);

  const {
    mountKey,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
  } = useFilterTableState({
    tabId: TABS.REDISCOVERY,
    totalRows: pagerTotalRows,
  });

  useEffect(() => {
    onTotalRowsChange?.(totalRows);
  }, [totalRows, onTotalRowsChange]);

  const tableLoading = isLoading || isFetching || isPlaceholderData;

  useEffect(() => {
    onLoadingChange?.(tableLoading);
  }, [tableLoading, onLoadingChange]);

  // Clear parent selection state when page or filters change (grid remounts)
  useEffect(() => {
    onClearSelections?.();
  }, [mountKey, currentPage, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useRediscoveryColumns({ userDateFormat });

  const {
    isPanelOpen,
    setIsPanelOpen,
    selectedRow,
    selectedRowIndex,
    popoverRef,
    previousRow,
    nextRow,
    candidatePrefetchBatch,
    handlePanelToggle,
    handleRowClick,
  } = useSidePanel({ rows });

  const { wrapperRef } = useStickyGridHeader({ refreshKey: mountKey });
  const savedColumnWidthsRef = useRef<Record<string, number>>({});

  return (
    <RediscoveryRowActionsModalProvider>
      <NoOverflowFullWidth>
        <div
          ref={(el) => {
            wrapperRef.current = el;
          }}
        >
          <BaseDataTable
            key={`${mountKey}-${clearSelectionsKey}`}
            rows={rows}
            columns={columns}
            totalRows={pagerTotalRows}
            loading={tableLoading}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            savedColumnWidthsRef={savedColumnWidthsRef}
            onRowSelected={onRowSelected}
            useDataGridPagination={false}
            slots={{
              noRowsOverlay: () => (
                <NoResultsOverlay
                  hasAnyFilters={hasAnyFilters}
                  onClearAll={onClearAll}
                  filteredHeader={t('no_results_header')}
                  filteredBody={t('no_results_body')}
                  clearButtonLabel={t('clear_all_filters')}
                  emptyHeader={t('no_candidates_header')}
                  emptyBody={t('no_candidates_body')}
                />
              ),
            }}
            onRowClick={(row: RowDef) => handleRowClick(row as Person)}
          />
        </div>

        {selectedRow && (
          <TalentRediscoverySidePanel
            ref={popoverRef}
            onClose={() => setIsPanelOpen(false)}
            onToggle={handlePanelToggle}
            applicationId={selectedRow.id}
            isVisible={isPanelOpen}
            currentIndex={selectedRowIndex}
            rowCount={rows.length}
            previousCandidate={previousRow}
            nextCandidate={nextRow}
            selectCandidate={handleRowClick}
            candidatePrefetchBatch={candidatePrefetchBatch}
          />
        )}
      </NoOverflowFullWidth>
    </RediscoveryRowActionsModalProvider>
  );
};

export default RediscoveryTableWrapper;
